import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { GatewayService } from '../../../domain/services/gateway/gateway.service';
import { GatewayLogRow } from '../../../domain/models/gateway.model';

@Component({
  selector: 'app-gateway-log-viewer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatTableModule,
  ],
  templateUrl: './gateway-log-viewer.component.html',
  styleUrl: './gateway-log-viewer.component.css',
})
export class GatewayLogViewerComponent implements OnInit {
  rangeStart: Date = new Date();
  rangeEnd: Date = new Date();
  transactionId = '';

  logs: GatewayLogRow[] = [];
  totalElements = 0;
  page = 0;
  size = 25;
  loading = false;
  expandedRow: GatewayLogRow | null = null;

  readonly cols = [
    'createdAt', 'processorEnv', 'endpoint', 'direction',
    'httpStatus', 'latencyMs', 'gatewayTransactionId', 'actor',
  ];
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private readonly gatewayService: GatewayService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.search();
  }

  search(): void {
    this.page = 0;
    this.expandedRow = null;
    this.load();
  }

  onPage(event: PageEvent): void {
    this.page = event.pageIndex;
    this.size = event.pageSize;
    this.load();
  }

  toggleRow(row: GatewayLogRow): void {
    this.expandedRow = this.expandedRow === row ? null : row;
  }

  isExpanded = (_index: number, row: GatewayLogRow): boolean => row === this.expandedRow;

  /** Pretty-print a JSON body; falls back to the raw string for non-JSON payloads. */
  prettyJson(body: string | null): string {
    if (!body) return '—';
    try {
      return JSON.stringify(JSON.parse(body), null, 2);
    } catch {
      return body;
    }
  }

  private load(): void {
    this.loading = true;
    this.gatewayService.getLogs({
      from: this.toIsoStart(this.rangeStart),
      to: this.toIsoEnd(this.rangeEnd),
      transactionId: this.transactionId.trim() || undefined,
      page: this.page,
      size: this.size,
    }).subscribe({
      next: (res) => {
        this.logs = res.content;
        this.totalElements = res.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.snackBar.open(err?.error?.message || 'Failed to load gateway logs.', 'Dismiss', { duration: 5000 });
      },
    });
  }

  private toIsoStart(d: Date): string {
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)).toISOString();
  }

  private toIsoEnd(d: Date): string {
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)).toISOString();
  }
}
